import { applyEconomiesOfScale, EOS_TIER } from './economies.js';
import {
  FOSSIL_SUBSIDY_MULTIPLIER,
  OPEX_FLOOR
} from '../data/constants.js';
import { FAMILY } from '../data/families.js';

const PRICE_SHOCK_MULTIPLIER = 2;

export function computePlantYearOpex({
  plant,
  baseOpex,
  subsidyActive,
  priceShockActive
}) {
  let opex = baseOpex;
  const isFossil = plant.family === FAMILY.FOSSIL_THERMAL;
  if (subsidyActive && isFossil) {
    opex *= FOSSIL_SUBSIDY_MULTIPLIER;
  }
  if (priceShockActive && isFossil) {
    opex *= PRICE_SHOCK_MULTIPLIER;
  }
  if (opex < OPEX_FLOOR) opex = OPEX_FLOOR;
  return opex;
}

export function computeYearOpex({
  portfolio,
  plantsById,
  subsidyActive,
  priceShockActive,
  eosDuplicateMultiplier,
  eosFirstCopyPaysFull
}) {
  const eos = applyEconomiesOfScale(
    portfolio,
    plantsById,
    eosDuplicateMultiplier,
    eosFirstCopyPaysFull
  );
  return eos.map((row) => {
    const plant = plantsById[row.id];
    const finalOpex = computePlantYearOpex({
      plant,
      baseOpex: row.postEos,
      subsidyActive,
      priceShockActive
    });
    return { ...row, finalOpex };
  });
}

export function totalYearOpex(opexRows) {
  return opexRows.reduce((s, r) => s + r.finalOpex, 0);
}

export function computeProspectivePlantOpex({
  plant,
  currentPortfolio,
  plantsById,
  subsidyActive,
  eosDuplicateMultiplier,
  eosFirstCopyPaysFull
}) {
  // The candidate goes on the end, so under the first-copy-pays-full
  // rule it is correctly treated as the additional copy - which is
  // what the player is being quoted for.
  const hypothetical = [...currentPortfolio, plant.id];
  const eos = applyEconomiesOfScale(
    hypothetical,
    plantsById,
    eosDuplicateMultiplier,
    eosFirstCopyPaysFull
  );
  const myRow = eos[eos.length - 1];
  const finalOpex = computePlantYearOpex({
    plant,
    baseOpex: myRow.postEos,
    subsidyActive,
    priceShockActive: false
  });
  const isFossil = plant.family === FAMILY.FOSSIL_THERMAL;
  const eosFromDuplicate = myRow.tier === EOS_TIER.DUPLICATE;
  const subsidyApplied = Boolean(subsidyActive && isFossil);
  const preClamp = subsidyApplied
    ? myRow.postEos * FOSSIL_SUBSIDY_MULTIPLIER
    : myRow.postEos;
  const floored = preClamp < OPEX_FLOOR;
  const discounted = finalOpex < plant.opex;
  return {
    base: plant.opex,
    eosTier: myRow.tier,
    eosMultiplier: myRow.multiplier,
    eosFromDuplicate,
    subsidyApplied,
    final: finalOpex,
    savings: plant.opex - finalOpex,
    discounted,
    floored
  };
}
